import { useState } from 'react';

import ProductCategoryPopup from './ProductCategoryPopup';
import EditProductCategoryPopup from './EditProductCategoryPopup';
import { useProductCategories } from '../../../hooks/useProductCategories';
import BasicDataTable from '../../DataTable/BasicDataTable';
import BasicSearchComponent from '../../DataTable/BasicSearchComponent';
import ActivationModal from '../../Modals/ActivationModal';

const ProductCategoryTable = () => {
  const {
    productCategories,
    handlerSearchByType,
    toggleProductCategoryStatus,
    handlerAddProductCategory,
  } = useProductCategories();

  // Estados para los modales
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: number;
    name: string;
    status: boolean;
  } | null>(null);
  const [isCategoryPopupOpen, setIsCategoryPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);

  // 📌 Función para manejar el registro de categorías de productos
  const handleRegisterCategory = (categoryData: {
    id: number;
    name: string;
  }) => {
    console.log('Categoría registrada:', categoryData);
    handlerAddProductCategory(categoryData);
  };

  // 📌 Función para manejar la edición de categorías de productos
  const handleUpdateCategory = (categoryData: { id: number; name: string }) => {
    console.log('Categoría actualizada:', categoryData);
    handlerAddProductCategory(categoryData);
  };

  // 📌 Función para abrir el modal de edición con la categoría seleccionada
  const handleEditCategory = (category: {
    id: number;
    name: string;
    status: boolean;
  }) => {
    console.log('Editando categoría:', category);
    setSelectedCategory(category);
    setIsEditPopupOpen(true);
  };

  // 📌 Columnas de la tabla
  const columns = [
    { Header: 'Nombre', accessor: 'name' },
    {
      Header: 'Estado',
      accessor: 'status',
      Cell: ({ value }) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
            value ? 'bg-green-500 text-white' : 'bg-red text-white'
          }`}
        >
          {value ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
  ];

  // 📌 Botón para abrir el modal
  const addButton = (
    <button
      className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-white hover:bg-opacity-90"
      onClick={() => setIsCategoryPopupOpen(true)}
    >
      + Nueva Categoría
    </button>
  );

  // 📌 Función para manejar la activación/desactivación de la categoría
  const handleToggleCategoryStatus = (category: {
    id: number;
    name: string;
    status: boolean;
  }) => {
    setSelectedCategory(category);
    setIsActivationModalOpen(true);
  };

  // 📌 Confirmar activación/desactivación
  const handleConfirmToggleStatus = () => {
    if (selectedCategory) {
      toggleProductCategoryStatus(selectedCategory.id);
    }
    setIsActivationModalOpen(false);
  };

  // 📌 Cancelar activación/desactivación
  const handleCancelToggle = () => {
    setIsActivationModalOpen(false);
    setSelectedCategory(null);
  };

  // 📌 Acciones para la tabla
  const actions = [
    { key: 'edit', onClick: handleEditCategory },
    { key: 'toggle', onClick: handleToggleCategoryStatus },
  ];

  return (
    <>
      <BasicDataTable
        columns={columns}
        data={productCategories}
        noDataMessage="No hay categorías disponibles."
        actions={actions}
        headerButton={addButton}
        searchComponent={
          <BasicSearchComponent onSearch={handlerSearchByType} />
        }
      />

      {/* Modal para activar/desactivar categoría */}
      <ActivationModal
        isOpen={isActivationModalOpen}
        title={
          selectedCategory?.status
            ? '¿Desactivar Categoría?'
            : '¿Activar Categoría?'
        }
        message={
          selectedCategory?.status
            ? '¿Estás seguro de que deseas desactivar esta categoría?'
            : '¿Estás seguro de que deseas activar esta categoría?'
        }
        confirmText={selectedCategory?.status ? 'Desactivar' : 'Activar'}
        cancelText="Cancelar"
        onConfirm={handleConfirmToggleStatus}
        onCancel={handleCancelToggle}
      />

      {/* Modal para agregar categoría */}
      <ProductCategoryPopup
        popupOpen={isCategoryPopupOpen}
        setPopupOpen={setIsCategoryPopupOpen}
        onRegister={handleRegisterCategory}
      />

      {/* Modal para editar categoría */}
      <EditProductCategoryPopup
        popupOpen={isEditPopupOpen}
        setPopupOpen={setIsEditPopupOpen}
        onUpdate={handleUpdateCategory}
        selectedCategory={selectedCategory}
      />
    </>
  );
};

export default ProductCategoryTable;
